import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { overlayVariants, panelVariants, sheetVariants, transition } from '../lib/motion.js';
import confetti from 'canvas-confetti';
import { searchDeezerTracks } from '../lib/deezer.js';
import { useEventStore } from '../store/useEventStore.js';
import {
  MAX_ACTIVE_SUGGESTIONS_PER_CLIENT,
  getCooldownRemainingMs,
  getDisplayName,
} from '../lib/profile.js';
import { getClientUid } from '../lib/uid.js';
import { toast } from '../store/useToast.js';
import {
  fetchEmbeddedCheckoutForPaidSuggestion,
  preparePaidSuggestionClientState,
} from '../lib/stripeCheckout.js';
import EmbeddedStripeCheckout from './EmbeddedStripeCheckout.jsx';
import { isPaidQueueUiEnabled } from '../lib/features.js';

const QUICK_BIDS = [
  { label: 'Sans montant', cents: 0 },
  { label: '0,20 €', cents: 20 },
  { label: '0,50 €', cents: 50 },
  { label: '1 €', cents: 100 },
];

function launchConfetti(isBoosted) {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }
  confetti({
    particleCount: isBoosted ? 140 : 70,
    spread: 75,
    startVelocity: 35,
    ticks: 130,
    scalar: isBoosted ? 1.05 : 0.9,
    zIndex: 2000,
    origin: { y: 0.7 },
    colors: isBoosted
      ? ['#ca8a04', '#f59e0b', '#f43f5e', '#fafaf9']
      : ['#ca8a04', '#a16207', '#fafaf9'],
  });
}

export default function SuggestModal({ open, onClose, onRequestProfile }) {
  const paidQueueUiEnabled = isPaidQueueUiEnabled();
  const suggestTrack = useEventStore((s) => s.suggestTrack);
  const event = useEventStore((s) => s.event);
  const barUser = useEventStore((s) => s.barUser);
  const myActiveCount = useEventStore((s) => s.myActiveRequestsCount());
  const boostThreshold = 500;
  const steps = paidQueueUiEnabled
    ? [
        { id: 1, label: 'Musique' },
        { id: 2, label: 'Montant' },
        { id: 3, label: 'Envoi' },
      ]
    : [
        { id: 1, label: 'Musique' },
        { id: 2, label: 'Envoi' },
      ];
  const lastStep = steps.length;

  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [bid, setBid] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [embeddedSecret, setEmbeddedSecret] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [cooldownMs, setCooldownMs] = useState(0);
  const titleRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep(1);
      setTitle('');
      setArtist('');
      setBid(0);
      setErrorMsg(null);
      setSearching(false);
      setSearchError(null);
      setMatches([]);
      setSelectedTrack(null);
      setCooldownMs(getCooldownRemainingMs());
      setEmbeddedSecret(null);
      setTimeout(() => titleRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const interval = setInterval(() => setCooldownMs(getCooldownRemainingMs()), 500);
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape' || !open) return;
      if (embeddedSecret) {
        setEmbeddedSecret(null);
        return;
      }
      onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, embeddedSecret]);

  useEffect(() => {
    if (!open || step !== 1) return undefined;
    const query = [title, artist].filter(Boolean).join(' ').trim();

    if (selectedTrack || query.length < 2) {
      setMatches([]);
      setSearchError(null);
      setSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError(null);
      try {
        const found = await searchDeezerTracks(query, 6);
        if (cancelled) return;
        setMatches(found);
        if (found.length === 0) setSearchError('Aucun résultat Deezer.');
      } catch {
        if (cancelled) return;
        setMatches([]);
        setSearchError('Recherche Deezer indisponible.');
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, step, title, artist, selectedTrack]);

  function applyDeezerMatch(track) {
    setTitle(track.title);
    setArtist(track.artist);
    setSelectedTrack(track);
    setMatches([]);
    setSearchError(null);
    setStep(2);
  }

  async function handleSubmit() {
    if (!selectedTrack?.id) {
      setErrorMsg('Choisis un morceau Deezer.');
      setStep(1);
      return;
    }
    if (cooldownMs > 0) {
      setErrorMsg(`Patiente ${Math.ceil(cooldownMs / 1000)}s avant la prochaine suggestion.`);
      return;
    }
    if (myActiveCount >= MAX_ACTIVE_SUGGESTIONS_PER_CLIENT) {
      setErrorMsg(
        `Tu as déjà ${MAX_ACTIVE_SUGGESTIONS_PER_CLIENT} suggestions en attente. Attends qu’elles soient jouées.`
      );
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    if (paidQueueUiEnabled && bid > 0) {
      if (!event?.id) {
        setSubmitting(false);
        setErrorMsg('Soirée introuvable.');
        return;
      }
      const returnUrl = window.location.href.split('#')[0];
      const payload = {
        eventId: event.id,
        clientUid: getClientUid(),
        barUserId: barUser?.id ?? null,
        amountCents: bid,
        title: title.trim(),
        artist: artist.trim(),
        deezerTrackId: selectedTrack.id,
        deezerPreviewUrl: selectedTrack.preview ?? null,
        deezerCoverUrl: selectedTrack.coverMedium ?? selectedTrack.cover ?? null,
      };
      const pay = await fetchEmbeddedCheckoutForPaidSuggestion(payload, returnUrl);
      setSubmitting(false);
      if (!pay.ok) {
        setErrorMsg(pay.message);
        return;
      }
      preparePaidSuggestionClientState(payload);
      setEmbeddedSecret(pay.clientSecret);
      return;
    }

    const res = await suggestTrack({
      title,
      artist,
      bidAmount: 0,
      deezerTrackId: selectedTrack.id,
      deezerPreviewUrl: selectedTrack.preview ?? null,
      deezerCoverUrl: selectedTrack.coverMedium ?? selectedTrack.cover ?? null,
    });
    setSubmitting(false);

    if (!res.ok) {
      const hint =
        res.reason === 'missing_fields'
          ? 'Renseigne un titre et un artiste.'
          : res.reason === 'error'
            ? [res.message, res.details].filter(Boolean).join(' — ') ||
              "Impossible d'envoyer la suggestion. Réessaie."
            : "Impossible d'envoyer la suggestion. Réessaie.";
      setErrorMsg(hint);
      return;
    }

    launchConfetti(false);
    toast.success('Suggestion envoyée ✨');
    onClose?.();
  }

  const willBoost = bid >= boostThreshold;
  const cooldownActive = cooldownMs > 0;
  const cooldownLabel = cooldownActive ? `${Math.ceil(cooldownMs / 1000)}s` : null;
  const reduceMotion = useReducedMotion();

  return (
    <AnimatePresence>
      {open && (
        <>
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-md p-0 sm:items-center sm:p-4"
          onClick={() => {
            if (!embeddedSecret) onClose?.();
          }}
          role="dialog"
          aria-modal="true"
          variants={overlayVariants}
          initial={reduceMotion ? false : 'initial'}
          animate="animate"
          exit={reduceMotion ? undefined : 'exit'}
          transition={transition(reduceMotion, 'fast')}
        >
          <motion.div
            className="sheet-surface max-h-[92dvh] w-full max-w-[480px] overflow-y-auto rounded-t-3xl border border-border p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
            variants={sheetVariants}
            initial={reduceMotion ? false : 'initial'}
            animate="animate"
            exit={reduceMotion ? undefined : 'exit'}
            transition={transition(reduceMotion, 'panel')}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="min-w-0">
                <h2 className="text-lg font-bold">Suggérer une musique</h2>
                <p className="text-[11px] text-white/50">
                  En tant que <span className="text-white/80">{barUser?.username ?? getDisplayName() ?? 'Anonyme'}</span> ·
                  <button
                    type="button"
                    onClick={() => onRequestProfile?.()}
                    className="ml-1 underline-offset-2 hover:underline"
                  >
                    changer
                  </button>
                </p>
              </div>
              <button
                type="button"
                className="btn-ghost h-8 w-8 px-0 py-0 text-white/60"
                onClick={() => {
                  if (embeddedSecret) setEmbeddedSecret(null);
                  else onClose?.();
                }}
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>

            <Stepper step={step} steps={steps} />

            <div className="mt-4 flex flex-col gap-3">
              <AnimatePresence mode="wait" initial={false}>
                {step === 1 && (
                  <Step1
                    key="step-1"
                    reduceMotion={reduceMotion}
                    titleRef={titleRef}
                    title={title}
                    setTitle={setTitle}
                    artist={artist}
                    setArtist={setArtist}
                    searching={searching}
                    searchError={searchError}
                    matches={matches}
                    onSelect={applyDeezerMatch}
                  />
                )}

                {paidQueueUiEnabled && step === 2 && selectedTrack && (
                  <Step2
                    key="step-2"
                    reduceMotion={reduceMotion}
                    selectedTrack={selectedTrack}
                    bid={bid}
                    setBid={setBid}
                    willBoost={willBoost}
                    onChangeMusic={() => {
                      setSelectedTrack(null);
                      setStep(1);
                    }}
                  />
                )}

                {selectedTrack && (paidQueueUiEnabled ? step === 3 : step === 2) && (
                  <Step3
                    key="step-3"
                    reduceMotion={reduceMotion}
                    selectedTrack={selectedTrack}
                    bid={bid}
                    willBoost={willBoost}
                    cooldownLabel={cooldownLabel}
                    freeRecapOnly={!paidQueueUiEnabled}
                    onChangeMusic={
                      paidQueueUiEnabled
                        ? undefined
                        : () => {
                            setSelectedTrack(null);
                            setStep(1);
                          }
                    }
                  />
                )}
              </AnimatePresence>

              {errorMsg && (
                <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {errorMsg}
                </p>
              )}

              <div className="flex items-center gap-2">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      const next = Math.max(1, step - 1);
                      // Revenir à la recherche : purger le morceau choisi, sinon la
                      // recherche reste gelée et l'envoi garde l'ancien deezerTrackId.
                      if (next === 1) setSelectedTrack(null);
                      setStep(next);
                    }}
                    className="btn-secondary min-h-[44px] px-4 py-3 text-sm"
                  >
                    ← Retour
                  </button>
                )}

                {step < lastStep && (
                  <button
                    type="button"
                    onClick={() => {
                      if (step === 1 && !selectedTrack) {
                        setErrorMsg('Choisis une musique pour continuer.');
                        return;
                      }
                      setErrorMsg(null);
                      setStep((s) => Math.min(lastStep, s + 1));
                    }}
                    disabled={step === 1 && !selectedTrack}
                    className="btn-primary flex-1 py-3 text-sm"
                  >
                    Continuer
                  </button>
                )}

                {step === lastStep && (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting || cooldownActive}
                    className="btn-primary flex-1 py-3 text-sm"
                  >
                    {submitting
                      ? 'Envoi…'
                      : cooldownActive
                        ? `Patiente ${cooldownLabel}`
                        : paidQueueUiEnabled && willBoost
                          ? '🔥 Suggérer avec paiement'
                          : 'Envoyer la suggestion'}
                  </button>
                )}
              </div>

              {cooldownActive && (
                <p className="text-center text-[11px] text-white/40">
                  Patiente {cooldownLabel} avant la prochaine suggestion.
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
        {embeddedSecret ? (
          <EmbeddedStripeCheckout
            clientSecret={embeddedSecret}
            onClose={() => setEmbeddedSecret(null)}
            onMountError={(msg) => {
              toast.error(msg);
              setEmbeddedSecret(null);
            }}
          />
        ) : null}
        </>
      )}
    </AnimatePresence>
  );
}

function Stepper({ step, steps }) {
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => {
        const active = step === s.id;
        const done = step > s.id;
        return (
          <div key={s.id} className="flex flex-1 items-center gap-2">
            <div
              className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold transition ${
                done
                  ? 'bg-emerald-400 text-black'
                  : active
                    ? 'bg-accent text-accent-fg'
                    : 'bg-white/5 text-white/40'
              }`}
            >
              {done ? '✓' : s.id}
            </div>
            <span
              className={`text-[11px] font-semibold uppercase tracking-wider ${
                active ? 'text-white' : 'text-white/40'
              }`}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <span className="flex-1 border-t border-dashed border-white/10" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Step1({
  reduceMotion,
  titleRef,
  title,
  setTitle,
  artist,
  setArtist,
  searching,
  searchError,
  matches,
  onSelect,
}) {
  return (
    <motion.div
      variants={panelVariants}
      initial={reduceMotion ? false : 'initial'}
      animate="animate"
      exit={reduceMotion ? undefined : 'exit'}
      transition={transition(reduceMotion, 'medium')}
      className="flex flex-col gap-3"
    >
      <div>
        <label className="mb-1 block text-xs font-medium text-white/60">Titre</label>
        <input
          ref={titleRef}
          className="input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="ex. Strobe"
          maxLength={120}
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-white/60">Artiste</label>
        <input
          className="input"
          value={artist}
          onChange={(e) => setArtist(e.target.value)}
          placeholder="ex. Deadmau5"
          maxLength={120}
        />
      </div>

      <div className="rounded-xl border border-border bg-bg-soft/50 p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-white/70">Résultats Deezer</p>
          <span className="text-[11px] text-white/50">{searching ? 'Recherche…' : 'Auto'}</span>
        </div>
        {searchError && (
          <p className="mt-2 text-[11px] text-amber-300/90">{searchError}</p>
        )}
        {matches.length > 0 && (
          <div className="mt-2 max-h-48 overflow-auto">
            <ul className="space-y-1.5">
              {matches.map((track) => (
                <li key={track.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(track)}
                    className="flex w-full items-center gap-2.5 rounded-lg border border-border bg-black/20 px-2.5 py-2 text-left hover:bg-white/5"
                  >
                    {track.cover ? (
                      <img
                        src={track.cover}
                        alt={`Pochette ${track.title}`}
                        className="h-10 w-10 shrink-0 rounded-md object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/5 text-xs text-white/40">
                        ♪
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-white">{track.title}</p>
                      <p className="truncate text-[11px] text-white/60">{track.artist}</p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function Step2({ reduceMotion, selectedTrack, bid, setBid, willBoost, onChangeMusic }) {
  return (
    <motion.div
      variants={panelVariants}
      initial={reduceMotion ? false : 'initial'}
      animate="animate"
      exit={reduceMotion ? undefined : 'exit'}
      transition={transition(reduceMotion, 'medium')}
      className="flex flex-col gap-3"
    >
      <SelectedTrackCard track={selectedTrack} onChangeMusic={onChangeMusic} />

      <div>
        <label className="mb-1 block text-xs font-medium text-white/60">
          Montant (optionnel)
        </label>
        <div className="grid grid-cols-3 gap-2">
          {QUICK_BIDS.map((opt) => {
            const active = bid === opt.cents;
            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => setBid(opt.cents)}
                className={`rounded-xl border px-2 py-2 text-xs font-medium transition-colors duration-200 ${
                  active
                    ? 'border-accent bg-accent/10 text-white'
                    : 'border-border bg-bg-soft text-white/70 hover:bg-white/5'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-center text-sm font-semibold text-amber-200">
          🔥 Le montant est versé à l’établissement & au DJ — il augmente la visibilité de ta piste dans
          la liste.
        </p>
        <p className="mt-1 text-center text-[11px] text-white/50">
          {willBoost
            ? '⚡ Au-delà de 5 €, ta suggestion passe en mode mis en avant.'
            : 'Plus le montant est élevé, plus ta suggestion remonte dans la liste.'}
        </p>
      </div>
    </motion.div>
  );
}

function Step3({
  reduceMotion,
  selectedTrack,
  bid,
  willBoost,
  cooldownLabel,
  freeRecapOnly,
  onChangeMusic,
}) {
  return (
    <motion.div
      variants={panelVariants}
      initial={reduceMotion ? false : 'initial'}
      animate="animate"
      exit={reduceMotion ? undefined : 'exit'}
      transition={transition(reduceMotion, 'medium')}
      className="flex flex-col gap-3"
    >
      <SelectedTrackCard track={selectedTrack} onChangeMusic={onChangeMusic} />

      <div className="rounded-xl border border-border bg-bg-soft/40 p-3">
        <p className="text-xs uppercase tracking-widest text-white/50">Récap</p>
        <ul className="mt-2 space-y-1 text-sm">
          {!freeRecapOnly ? (
            <>
              <li className="flex justify-between">
                <span className="text-white/60">Montant</span>
                <span className={bid > 0 ? 'font-mono font-semibold tabular-nums text-gold' : 'font-semibold text-white/70'}>
                  {bid > 0 ? `${(bid / 100).toFixed(2)} €` : 'Aucun'}
                </span>
              </li>
              <li className="flex justify-between">
                <span className="text-white/60">Mode</span>
                <span className={`font-semibold ${willBoost ? 'text-gold' : 'text-white'}`}>
                  {willBoost ? '⚡ Mis en avant' : 'Standard'}
                </span>
              </li>
            </>
          ) : (
            <li className="flex justify-between">
              <span className="text-white/60">Envoi</span>
              <span className="font-semibold text-white">Suggestion gratuite</span>
            </li>
          )}
          {cooldownLabel && (
            <li className="flex justify-between">
              <span className="text-white/60">Cooldown</span>
              <span className="font-semibold text-amber-300">{cooldownLabel}</span>
            </li>
          )}
        </ul>
      </div>
    </motion.div>
  );
}

function SelectedTrackCard({ track, onChangeMusic }) {
  return (
    <div className="rounded-xl border border-accent/30 bg-accent/10 p-3">
      <p className="font-mono text-xs uppercase tracking-widest text-accent">
        Morceau sélectionné
      </p>
      <div className="mt-2 flex items-center gap-2.5">
        {track.cover ? (
          <img
            src={track.cover}
            alt={`Pochette ${track.title}`}
            className="h-12 w-12 rounded-md object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-white/10 text-white/50">
            ♪
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{track.title}</p>
          <p className="truncate text-xs text-white/70">{track.artist}</p>
        </div>
        {onChangeMusic && (
          <button
            type="button"
            onClick={onChangeMusic}
            className="shrink-0 rounded-full border border-border bg-white/5 px-2.5 py-1 text-[11px] text-white/70 hover:bg-white/10"
          >
            Changer
          </button>
        )}
      </div>
    </div>
  );
}
